/** Shared date/time formatting helpers. */

const BRUSSELS = "Europe/Brussels";

function brusselsDayString(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BRUSSELS,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/**
 * Return the number of whole calendar days (Europe/Brussels) until the given
 * ISO end date. Returns null when the input is unparseable; negative when the
 * date is in the past.
 */
export function daysUntilInBrussels(endIso: string): number | null {
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  const endUtc = Date.parse(brusselsDayString(end) + "T00:00:00Z");
  const nowUtc = Date.parse(brusselsDayString(now) + "T00:00:00Z");
  if (Number.isNaN(endUtc) || Number.isNaN(nowUtc)) return null;
  return Math.round((endUtc - nowUtc) / 86_400_000);
}

/**
 * Format a byte count as `MB` (1 decimal) or `KB` (rounded). Returns "—" for
 * null/undefined/zero. Decimal (1000-free) units matching the recordings UI.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/**
 * Convert a Loki nanosecond timestamp (as a string) to a `Date`. Uses BigInt
 * division rather than `parseInt`/`Number` on the raw string so precision
 * survives values beyond `Number.MAX_SAFE_INTEGER`.
 */
export function dateFromNsTimestamp(tsNs: string): Date {
  return new Date(Number(BigInt(tsNs) / 1_000_000n));
}

type DateTimeOpts = { seconds?: boolean; timeZone?: string };

/**
 * Format an ISO timestamp as nl-BE `dd-MM-yyyy HH:mm` (or `HH:mm:ss` when
 * `seconds` is set). Returns "—" for null/undefined.
 */
export function formatDateTime(
  iso: string | null | undefined,
  opts: DateTimeOpts = {},
): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    ...(opts.seconds ? { second: "2-digit" as const } : {}),
  });
}
